<div class="subscription-container">
  <!-- Header Section -->
  <div class="header-section">
    <div class="header-content">
      <div class="title-section">
        <h1 class="page-title">Client Subscriptions</h1>
        <p class="page-subtitle">Manage and monitor client subscription status</p>
      </div>

      <div class="header-actions">
        <button
          class="view-toggle-btn"
          (click)="toggleViewMode()"
          [class.active]="viewMode === 'grid'">
          <mat-icon>{{ viewMode === 'grid' ? 'view_list' : 'grid_view' }}</mat-icon>
          <span>{{ viewMode === 'grid' ? 'List View' : 'Grid View' }}</span>
        </button>
      </div>
    </div>

    <!-- Search Section -->
    <div class="search-section">
      <div class="search-container" [class.focused]="searchFocused">
        <div class="search-icon">
          <mat-icon>search</mat-icon>
        </div>
        <input
          type="text"
          class="search-input"
          placeholder="Search clients by name, email, company, or status..."
          [(ngModel)]="searchQuery"
          (input)="onSearchQueryChange()"
          (focus)="onSearchFocus()"
          (blur)="onSearchBlur()"
        />
        <button
          *ngIf="searchQuery"
          class="clear-search-btn"
          (click)="clearSearch()">
          <mat-icon>close</mat-icon>
        </button>
      </div>

      <!-- Search Results Info -->
      <div class="search-results-info" *ngIf="searchQuery">
        <p>
          Found {{ filteredSubscriptionData.length }} result{{ filteredSubscriptionData.length !== 1 ? 's' : '' }}
          <span *ngIf="searchQuery"> for "{{ searchQuery }}"</span>
        </p>
      </div>
    </div>
  </div>

  <!-- Loading State -->
  <div *ngIf="subscriptionLoading" class="loading-container">
    <div class="loading-grid" [class.list-loading]="viewMode === 'list'">
      <div class="skeleton-card" *ngFor="let item of [1,2,3,4,5,6]">
        <div class="skeleton-shimmer"></div>
      </div>
    </div>
  </div>

  <!-- Empty State -->
  <div *ngIf="!subscriptionLoading && filteredSubscriptionData.length === 0" class="empty-state">
    <div class="empty-icon">
      <mat-icon>{{ searchQuery ? 'search_off' : 'inbox' }}</mat-icon>
    </div>
    <h3>{{ searchQuery ? 'No results found' : 'No Subscriptions Found' }}</h3>
    <p>
      {{ searchQuery
        ? 'Try adjusting your search criteria or clear the search to see all subscriptions.'
        : 'There are no client subscriptions to display at this time.' }}
    </p>
    <button
      *ngIf="searchQuery"
      class="clear-search-btn-empty"
      (click)="clearSearch()">
      Clear Search
    </button>
  </div>

  <!-- Grid View -->
  <div
    *ngIf="!subscriptionLoading && filteredSubscriptionData.length > 0 && viewMode === 'grid'"
    class="grid-container">
    <div
      class="subscription-card"
      *ngFor="let client of filteredSubscriptionData; let i = index"
      [style.animation-delay.ms]="i * 100">

      <!-- Card Header -->
      <div class="card-header">
        <div class="client-info">
          <div class="avatar-container">
            <img
              *ngIf="client.image"
              [src]="client.image.trim()"
              [alt]="client.full_name"
              class="client-avatar"
              onerror="this.style.display='none'; this.nextElementSibling.style.display='flex';">
            <div class="avatar-fallback" [style.display]="client.image ? 'none' : 'flex'">
              {{ client.full_name.charAt(0).toUpperCase() }}
            </div>
          </div>
          <div class="client-details">
            <h3 class="client-name">{{ client.full_name }}</h3>
            <p class="company-name">{{ client.company_name }}</p>
          </div>
        </div>

        <div class="status-badge" [ngClass]="getStatusBadgeClass(client.subscription_flag)">
          {{ client.subscription_flag }}
        </div>
      </div>

      <!-- Card Content -->
      <div class="card-content">
        <div class="info-row">
          <mat-icon class="info-icon">email</mat-icon>
          <span class="info-text">{{ client.email }}</span>
        </div>

        <div class="info-row">
          <mat-icon class="info-icon">phone</mat-icon>
          <span class="info-text">{{ client.phone_no }}</span>
        </div>

        <div class="info-row">
          <mat-icon class="info-icon">location_on</mat-icon>
          <span class="info-text">{{ client.address }}</span>
        </div>

        <div class="subscription-details">
          <div class="detail-item">
            <span class="detail-label">Subscription Date</span>
            <span class="detail-value">
              {{ client.subscription_date ? (client.subscription_date | date:'shortDate') : 'N/A' }}
            </span>
          </div>

          <div class="detail-item">
            <span class="detail-label">Days Left</span>
            <span class="detail-value" [ngClass]="getDaysLeftColor(client.days_left)">
              {{ client.days_left ?? 'N/A' }}
            </span>
          </div>
        </div>
      </div>

      <!-- Card Footer -->
      <div class="card-footer">
        <button
          class="generate-key-btn"
          [class.loading]="generatingKey[client.clientId]"
          [disabled]="generatingKey[client.clientId] || client.active"
          (click)="generateSubscriptionKey(client.clientId)">
          <mat-icon *ngIf="!generatingKey[client.clientId]">vpn_key</mat-icon>
          <div *ngIf="generatingKey[client.clientId]" class="loading-spinner"></div>
          <span>{{ generatingKey[client.clientId] ? 'Generating...' : 'Generate Key' }}</span>
        </button>

        <div class="active-indicator" [class.active]="client.active === 1">
          <div class="indicator-dot"></div>
          <span>{{ client.active === 1 ? 'Active' : 'Inactive' }}</span>
        </div>
      </div>
    </div>
  </div>

  <!-- List View -->
  <div
    *ngIf="!subscriptionLoading && filteredSubscriptionData.length > 0 && viewMode === 'list'"
    class="list-container">

    <!-- List Header -->
    <div class="list-header">
      <div class="header-col client-col">Client</div>
      <div class="header-col">Status</div>
      <div class="header-col">Subscription</div>
      <div class="header-col">Days Left</div>
      <div class="header-col">Actions</div>
    </div>

    <!-- List Items -->
    <div class="list-items">
      <div
        class="list-item"
        *ngFor="let client of filteredSubscriptionData; let i = index"
        [style.animation-delay.ms]="i * 50">

        <div class="list-col client-col">
          <div class="client-info-list">
            <div class="avatar-container-small">
              <img
                *ngIf="client.image"
                [src]="client.image.trim()"
                [alt]="client.full_name"
                class="client-avatar-small"
                onerror="this.style.display='none'; this.nextElementSibling.style.display='flex';">
              <div class="avatar-fallback-small" [style.display]="client.image ? 'none' : 'flex'">
                {{ client.full_name.charAt(0).toUpperCase() }}
              </div>
            </div>
            <div class="client-details-list">
              <h4 class="client-name-list">{{ client.full_name }}</h4>
              <p class="company-name-list">{{ client.company_name }}</p>
              <p class="client-email">{{ client.email }}</p>
            </div>
          </div>
        </div>

        <div class="list-col">
          <div class="status-container">
            <div class="status-badge-list status-badge" [ngClass]="getStatusBadgeClass(client.subscription_flag)">
              {{ client.subscription_flag }}
            </div>
            <div class="active-indicator-list" [class.active]="client.active === 1">
              <div class="indicator-dot-small"></div>
              <span>{{ client.active === 1 ? 'Active' : 'Inactive' }}</span>
            </div>
          </div>
        </div>

        <div class="list-col">
          <div class="subscription-info">
            <div class="subscription-date">
              {{ client.subscription_date ? (client.subscription_date | date:'shortDate') : 'N/A' }}
            </div>
            <div class="expiry-date">
              Expires: {{ client.expiry_date ? (client.expiry_date | date:'shortDate') : 'N/A' }}
            </div>
          </div>
        </div>

        <div class="list-col">
          <span class="days-left-list" [ngClass]="getDaysLeftColor(client.days_left)">
            {{ client.days_left ?? 'N/A' }}
          </span>
        </div>

        <div class="list-col">
          <button
            class="generate-key-btn-list"
            [class.loading]="generatingKey[client.clientId]"
            [disabled]="generatingKey[client.clientId] || client.active"
            (click)="generateSubscriptionKey(client.clientId)">
            <mat-icon *ngIf="!generatingKey[client.clientId]">vpn_key</mat-icon>
            <div *ngIf="generatingKey[client.clientId]" class="loading-spinner-small"></div>
            <span>{{ generatingKey[client.clientId] ? 'Generating...' : 'Generate Key' }}</span>
          </button>
        </div>
      </div>
    </div>
  </div>
</div>
